// Bank & cash: the company's money accounts and their balances (posted entries only).
import { useState, useEffect, useCallback } from "react";
import { Button, Notice, Modal, Field, Input, Select } from "./kit/components";
import { T, text } from "./kit/tokens";
import { showToast } from "../ui/core";
import { moneyBalances, saveMoneyAccount } from "./api";
import { errText } from "./shared";
import { npr, todayIso, DateChip } from "./workflow";

export default function MoneyAccounts({ ctx }) {
    const { data, cal, display, mobile, reload, version } = ctx;
    const [bal, setBal] = useState({});
    const [err, setErr] = useState("");
    const [edit, setEdit] = useState(null);
    const load = useCallback(async () => {
        try { setErr(""); const r = await moneyBalances(todayIso()); setBal(Object.fromEntries(r.map(x => [x.money_account_id, x]))); } catch (e) { setErr(errText(e)); }
    }, []);
    useEffect(() => { load(); }, [load, version]);
    const linked = new Set(data.moneyAccounts.map(m => m.account_id));
    const rbb = data.accounts.find(a => a.code === "1121"), cash = data.accounts.find(a => a.code === "1110");
    return (
        <div>
            <Notice tone="info">The accounts Foxbeep's money actually sits in. Balances come only from posted entries. Only the last 4 digits of an account number are kept. Add Cash on Hand only if the company really handles cash.</Notice>
            <div style={{ height: 14 }} />
            {err && <Notice tone="danger">{err}</Notice>}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                {rbb && !linked.has(rbb.id) && <Button size="sm" onClick={() => setEdit({ account_id: rbb.id, kind: "bank", display_name: "Rastriya Banijya Bank", institution: "Rastriya Banijya Bank" })}>Add Rastriya Banijya Bank</Button>}
                {cash && !linked.has(cash.id) && <Button size="sm" variant="secondary" onClick={() => setEdit({ account_id: cash.id, kind: "cash", display_name: "Cash on Hand" })}>Add Cash on Hand (only if used)</Button>}
                <Button size="sm" variant="secondary" onClick={() => setEdit({ kind: "bank", display_name: "" })}>Add another bank or wallet</Button>
            </div>
            {data.moneyAccounts.length === 0 ? <div style={text.caption}>No money accounts yet.</div> : (
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
                    {data.moneyAccounts.map(m => {
                        const b = bal[m.id];
                        const acct = data.accounts.find(a => a.id === m.account_id);
                        return (
                            <div key={m.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 16, display: "grid", gap: 6, boxShadow: T.shadow, opacity: m.active ? 1 : 0.6 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                    <b style={{ color: T.textStrong }}>{m.display_name}</b>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.6 }}>{m.kind}{m.active ? "" : " · inactive"}</span>
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 700, color: Number(b?.balance) < 0 ? T.warning : T.textStrong, fontVariantNumeric: "tabular-nums" }}>{npr(b?.balance)}</div>
                                <div style={text.caption}>{[m.institution, m.branch, m.account_last4 ? `•••• ${m.account_last4}` : null, acct ? `ledger ${acct.code}` : null].filter(Boolean).join(" · ")}</div>
                                <div style={text.caption}>Last activity: {b?.last_activity ? <DateChip iso={b.last_activity} cal={cal} display={display} /> : "none"}</div>
                                <div><Button size="sm" variant="ghost" onClick={() => setEdit(m)}>Edit</Button></div>
                            </div>);
                    })}
                </div>
            )}
            <MoneyAccountModal value={edit} onClose={() => setEdit(null)} onDone={async () => { setEdit(null); await reload(); await load(); }} />
        </div>
    );
}

function MoneyAccountModal({ value, onClose, onDone }) {
    const [f, setF] = useState({});
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => { if (value) { setF({ ...value, account_last4: value.account_last4 || "", institution: value.institution || "", branch: value.branch || "", active: value.active !== false }); setErr(""); setBusy(false); } }, [value]);
    if (!value) return null;
    const isNew = !value.id;
    const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
    const save = async () => {
        setErr("");
        if (!f.display_name?.trim()) { setErr("Enter a name."); return; }
        if (f.account_last4 && !/^\d{4}$/.test(f.account_last4)) { setErr("Enter exactly the last 4 digits, or leave it empty."); return; }
        setBusy(true);
        try { await saveMoneyAccount({ id: f.id, account_id: f.account_id, kind: f.kind, display_name: f.display_name, institution: f.institution, branch: f.branch, account_last4: f.account_last4, active: f.active }); showToast("Saved"); await onDone(); }
        catch (e) { setErr(errText(e)); setBusy(false); }
    };
    return (
        <Modal open title={isNew ? "Add a money account" : `Edit ${value.display_name}`} onClose={busy ? undefined : onClose}
            footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Button></>}>
            <div style={{ display: "grid", gap: 14 }}>
                {isNew && !value.account_id && <Notice tone="info">A new ledger account is created for it under Cash & Bank.</Notice>}
                {isNew && (
                    <Field label="Type"><Select value={f.kind} onChange={set("kind")} disabled={!!value.account_id}>
                        <option value="bank">Bank account</option><option value="wallet">Digital wallet held by the company</option><option value="cash">Cash</option>
                    </Select></Field>
                )}
                <Field label="Name" required><Input value={f.display_name || ""} onChange={set("display_name")} /></Field>
                {f.kind !== "cash" && <>
                    <Field label="Bank or provider"><Input value={f.institution || ""} onChange={set("institution")} /></Field>
                    <Field label="Branch"><Input value={f.branch || ""} onChange={set("branch")} /></Field>
                    <Field label="Last 4 digits of the account number" hint="Only the last 4 digits are stored, never the full number.">
                        <Input value={f.account_last4 || ""} inputMode="numeric" maxLength={4} onChange={e => setF(x => ({ ...x, account_last4: e.target.value.replace(/\D/g, "") }))} /></Field>
                </>}
                {!isNew && <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, color: T.text }}><input type="checkbox" checked={!!f.active} onChange={set("active")} /> Active</label>}
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}
